import type { ModuleId } from "@/modules/registry";
import type { Identifier } from "ra-core";
import { getContactShowPath } from "@/app/routing";
import { getMessagesConversationPath } from "@/modules/messages/messagesRouting";
import type { ClientScope } from "@/modules/clients/clientScope";

/** Row of the `client_activity_timeline` view. */
export type ClientTimelineEvent = {
  id: string;
  occurred_at: string;
  kind: ClientTimelineKind;
  action: string;
  title: string | null;
  detail: string | null;
  direction: string | null;
  amount: number | null;
  company_id: Identifier | null;
  contact_id: Identifier | null;
  deal_id: Identifier | null;
  ref_resource: string;
  ref_id: Identifier;
  actor_member_id: Identifier | null;
};

export type ClientTimelineKind =
  | "contact"
  | "note"
  | "task"
  | "meeting"
  | "ticket"
  | "ticket_note"
  | "ticket_message"
  | "email"
  | "sms"
  | "call"
  | "team_message"
  | "proposal"
  | "invoice"
  | "contract"
  | "project"
  | "subscription";

export type ClientTimelineFilter =
  | "all"
  | "notes"
  | "tasks"
  | "emails"
  | "messages"
  | "tickets"
  | "billing"
  | "projects";

export const CLIENT_TIMELINE_FILTERS: {
  id: ClientTimelineFilter;
  label: string;
  kinds: ClientTimelineKind[] | null;
}[] = [
  { id: "all", label: "All", kinds: null },
  { id: "notes", label: "Notes", kinds: ["note", "ticket_note"] },
  { id: "tasks", label: "Tasks & meetings", kinds: ["task", "meeting"] },
  { id: "emails", label: "Emails", kinds: ["email"] },
  {
    id: "messages",
    label: "Texts & calls",
    kinds: ["sms", "call", "team_message"],
  },
  {
    id: "tickets",
    label: "Tickets",
    kinds: ["ticket", "ticket_message", "ticket_note"],
  },
  {
    id: "billing",
    label: "Billing",
    kinds: ["proposal", "invoice", "contract", "subscription"],
  },
  { id: "projects", label: "Projects", kinds: ["project"] },
];

/** PostgREST filter for one scope and kind filter. */
/** App each timeline source belongs to (sources not listed are core). */
export const CLIENT_TIMELINE_RESOURCE_MODULES: Record<string, ModuleId> = {
  deals: "pipeline",
  deal_notes: "pipeline",
  calendar_events: "calendar",
  tickets: "tickets",
  conversations: "messages",
  mail_threads: "mail",
  voice_calls: "messages",
  proposals: "proposals",
  client_invoices: "invoices",
  client_subscriptions: "invoices",
  contracts: "contracts",
};

/** Timeline sources of apps the company has off (left out of the feed). */
export const timelineResourcesOff = (isEnabled: (id: ModuleId) => boolean) =>
  Object.entries(CLIENT_TIMELINE_RESOURCE_MODULES)
    .filter(([, moduleId]) => !isEnabled(moduleId))
    .map(([resource]) => resource);

export const buildClientTimelineFilter = (
  scope: ClientScope,
  filter: ClientTimelineFilter,
  /** Only this project's events (the project's Activities tab). */
  dealId?: Identifier | null,
  /** Sources to leave out (apps the company has off). */
  excludedResources: string[] = [],
) => {
  const base =
    dealId != null
      ? { "deal_id@eq": dealId }
      : scope.kind === "company"
        ? { "company_id@eq": scope.companyId }
        : { "contact_id@eq": scope.contactId };
  const kinds = CLIENT_TIMELINE_FILTERS.find(
    (entry) => entry.id === filter,
  )?.kinds;
  const scoped = excludedResources.length
    ? {
        ...base,
        "ref_resource@not.in": `(${excludedResources.join(",")})`,
      }
    : base;
  return kinds ? { ...scoped, "kind@in": `(${kinds.join(",")})` } : scoped;
};

const ACTION_LABELS: Record<string, string> = {
  "contact:created": "Contact added",
  "note:created": "Note",
  "task:created": "Task created",
  "task:completed": "Task completed",
  "meeting:scheduled": "Meeting scheduled",
  "meeting:completed": "Meeting completed",
  "ticket:opened": "Ticket opened",
  "ticket_note:internal": "Internal note on ticket",
  "ticket_message:inbound": "Ticket reply received",
  "ticket_message:outbound": "Ticket reply sent",
  "email:inbound": "Email received",
  "email:outbound": "Email sent",
  "sms:inbound": "Text received",
  "sms:outbound": "Text sent",
  "sms:internal": "Internal note on conversation",
  "call:inbound": "Incoming call",
  "call:outbound": "Outgoing call",
  "proposal:created": "Proposal created",
  "proposal:sent": "Proposal sent",
  "proposal:accepted": "Proposal accepted",
  "proposal:rejected": "Proposal declined",
  "invoice:created": "Invoice created",
  "invoice:sent": "Invoice sent",
  "invoice:paid": "Payment received",
  "team_message:posted": "Team message",
  "team_message:internal": "Internal note",
  "invoice:void": "Invoice voided",
  "contract:created": "Contract created",
  "contract:signed": "Contract signed",
  "project:created": "Project created",
  "subscription:created": "Subscription created",
  "subscription:activated": "Subscription activated",
  "subscription:canceled": "Subscription canceled",
};

export const clientTimelineLabel = (event: ClientTimelineEvent) =>
  ACTION_LABELS[`${event.kind}:${event.action}`] ??
  `${event.kind} ${event.action}`.replace(/_/g, " ");

/** Where an event opens. Null when there is no dedicated page. */
export const clientTimelineHref = (
  event: ClientTimelineEvent,
): string | null => {
  switch (event.ref_resource) {
    case "contacts":
    case "contact_notes":
      return event.contact_id != null
        ? getContactShowPath(event.contact_id)
        : null;
    case "deal_notes":
    case "deals":
      return event.deal_id != null ? `/deals/${event.deal_id}/show` : null;
    case "tasks":
      return event.deal_id != null
        ? `/deals/${event.deal_id}/show`
        : event.contact_id != null
          ? `${getContactShowPath(event.contact_id)}?section=tasks`
          : null;
    case "calendar_events":
      return "/calendar";
    case "tickets":
      return `/tickets/${event.ref_id}/show`;
    case "conversations":
      // Project team chat lives in the project's Messages tab.
      if (event.kind === "team_message" && event.deal_id != null) {
        return `/deals/${event.deal_id}/show?tab=messages`;
      }
      return getMessagesConversationPath(event.ref_id);
    case "mail_threads":
      return `/mail?thread=${event.ref_id}`;
    case "voice_calls":
      return "/messages";
    case "proposals":
      return `/proposals/${event.ref_id}/preview`;
    case "client_invoices":
      return `/billing/invoices/${event.ref_id}/show`;
    case "contracts":
      return `/contracts/${event.ref_id}/show`;
    case "client_subscriptions":
      return "/billing";
    default:
      return null;
  }
};

/** Module(s) a timeline filter belongs to; filters not listed are core. */
export const CLIENT_TIMELINE_FILTER_MODULES: Partial<
  Record<ClientTimelineFilter, ModuleId[]>
> = {
  emails: ["mail"],
  messages: ["messages"],
  tickets: ["tickets"],
  billing: ["invoices", "proposals", "contracts"],
  projects: ["pipeline"],
};
